import { useState, useEffect } from 'react'
import { db } from '../firebase/config'
import { collection, getDocs, addDoc, doc, updateDoc, increment, Timestamp, query, where } from 'firebase/firestore'
import { Search, Heart, Music, Calendar, Send } from 'lucide-react'

// ─── Champ de formulaire ──────────────────────────────────
function Field({ label, required, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontWeight: 600, fontSize: 'clamp(0.82rem,1.6vw,0.92rem)', color: 'rgba(255,255,255,0.85)' }}>
        {label} {required && <span style={{ color: '#ff1493' }}>*</span>}
      </label>
      {children}
    </div>
  )
}

const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  padding: 'clamp(10px,1.8vh,13px) clamp(12px,2vw,15px)',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 12, color: '#fff',
  fontSize: 'clamp(0.88rem,1.7vw,1rem)', outline: 'none',
  fontFamily: 'inherit', transition: 'border-color 0.2s'
}

// ─── Carte dédicace ───────────────────────────────────────
function DedicaceCard({ dedicace, isArchive, onLike }) {
  const formatDate = (date) => {
    if (!date) return ''
    try {
      const d = date.toDate ? date.toDate() : new Date(date)
      return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    } catch { return '' }
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(255,20,147,0.06), rgba(0,217,255,0.04))',
      border: '1px solid rgba(255,20,147,0.2)',
      borderRadius: 18,
      padding: 'clamp(14px,2.5vh,20px)',
      display: 'flex', flexDirection: 'column',
      gap: 'clamp(10px,1.8vh,14px)'
    }}>

      {/* En-tête DE → À + like */}
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 10,
        flexWrap: 'wrap',
        paddingBottom: 'clamp(10px,1.8vh,14px)',
        borderBottom: '1px solid rgba(255,255,255,0.08)'
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          fontSize: 'clamp(0.95rem,2vw,1.15rem)', fontWeight: 800
        }}>
          <span style={{ color: '#ff1493' }}>❤️ {dedicace.from}</span>
          <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}>→</span>
          <span style={{ color: '#00d9ff' }}>{dedicace.to}</span>
        </div>

        <button
          onClick={() => onLike(dedicace.id, isArchive)}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: 'clamp(7px,1.2vh,9px) clamp(12px,1.8vw,16px)',
            background: 'rgba(255,20,147,0.14)',
            border: '1px solid rgba(255,20,147,0.35)',
            borderRadius: 20, color: '#ff1493',
            fontWeight: 700, fontSize: 'clamp(0.82rem,1.6vw,0.9rem)',
            cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,20,147,0.28)'; e.currentTarget.style.transform = 'scale(1.06)' }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,20,147,0.14)'; e.currentTarget.style.transform = 'scale(1)' }}
        >
          <Heart size={14} /> {dedicace.likes || 0}
        </button>
      </div>

      {/* Message — sauts de ligne respectés */}
      <p style={{
        fontSize: 'clamp(0.92rem,1.8vw,1.05rem)',
        lineHeight: 1.8, fontStyle: 'italic',
        color: 'rgba(255,255,255,0.9)',
        whiteSpace: 'pre-line', /* ← sauts de ligne natifs */
        margin: 0,
        padding: 'clamp(10px,1.8vh,15px) clamp(12px,2vw,16px)',
        background: 'rgba(255,255,255,0.03)',
        borderRadius: '0 12px 12px 12px',
        borderLeft: '3px solid #ff1493'
      }}>
        "{dedicace.message}"
      </p>

      {/* Chanson */}
      {dedicace.song && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: 'clamp(8px,1.4vh,11px) clamp(12px,1.8vw,15px)',
          background: 'rgba(255,215,0,0.1)',
          border: '1px solid rgba(255,215,0,0.2)',
          borderRadius: 10
        }}>
          <Music size={15} color="#ffd700" />
          <span style={{ color: '#ffd700', fontWeight: 700, fontSize: 'clamp(0.82rem,1.6vw,0.92rem)' }}>
            {dedicace.song}
          </span>
        </div>
      )}

      {/* Footer date */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 'clamp(8px,1.5vw,14px)', flexWrap: 'wrap',
        paddingTop: 'clamp(8px,1.4vh,12px)',
        borderTop: '1px solid rgba(255,255,255,0.07)',
        fontSize: 'clamp(0.72rem,1.4vw,0.82rem)',
        color: 'rgba(255,255,255,0.42)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Calendar size={12} /> {formatDate(dedicace.createdAt || dedicace.archivedAt)}
        </div>
        {isArchive && dedicace.archivedReason && (
          <div style={{ color: 'rgba(255,165,0,0.65)' }}>📦 {dedicace.archivedReason}</div>
        )}
      </div>
    </div>
  )
}

// ─── Page principale ──────────────────────────────────────
function DedicacesPage({ currentUser }) {
  const [dedicaces, setDedicaces]       = useState([])
  const [oldDedicaces, setOldDedicaces] = useState([])
  const [loading, setLoading]           = useState(true)
  const [searchTerm, setSearchTerm]     = useState('')
  const [currentPage, setCurrentPage]   = useState(1)
  const [activeTab, setActiveTab]       = useState('recent')
  const [showForm, setShowForm]         = useState(false)
  const [formData, setFormData]         = useState({ from: '', to: '', message: '', song: '' })
  const [submitting, setSubmitting]     = useState(false)

  const ITEMS_PER_PAGE = 10

  useEffect(() => { loadDedicaces() }, [])

  const sortByDate = (arr, field = 'createdAt') =>
    [...arr].sort((a, b) => {
      const toDate = x => x?.toDate ? x.toDate() : new Date(x || 0)
      return toDate(b[field]) - toDate(a[field])
    })

  const loadDedicaces = async () => {
    setLoading(true)
    try {
      const [recentSnap, archivesSnap] = await Promise.all([
        getDocs(query(collection(db, '100johnny', 'main', 'dedicaces'), where('approved', '==', true))),
        getDocs(collection(db, '100johnny', 'main', 'old_dedicaces'))
      ])
      setDedicaces(sortByDate(recentSnap.docs.map(d => ({ id: d.id, ...d.data() }))))
      setOldDedicaces(sortByDate(archivesSnap.docs.map(d => ({ id: d.id, ...d.data() })), 'archivedAt'))
    } catch (err) {
      console.error('Erreur chargement dédicaces:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!currentUser) { alert('⚠️ Vous devez être connecté pour envoyer une dédicace'); return }
    if (!formData.from || !formData.to || !formData.message) { alert('⚠️ Veuillez remplir tous les champs obligatoires'); return }
    setSubmitting(true)
    try {
      await addDoc(collection(db, '100johnny', 'main', 'dedicaces'), {
        from: formData.from, to: formData.to,
        message: formData.message, song: formData.song || null,
        userId: currentUser.uid, authorId: currentUser.uid,
        authorName: currentUser.displayName || currentUser.email,
        createdAt: Timestamp.now(), approved: false, likes: 0, emailSent: false
      })
      alert('✅ Votre dédicace a été envoyée ! Elle sera visible après validation.')
      setFormData({ from: '', to: '', message: '', song: '' })
      setShowForm(false)
    } catch (err) {
      console.error(err); alert('❌ Erreur lors de l\'envoi de la dédicace')
    } finally { setSubmitting(false) }
  }

  const handleLike = async (id, isArchive) => {
    try {
      await updateDoc(doc(db, '100johnny', 'main', isArchive ? 'old_dedicaces' : 'dedicaces', id), { likes: increment(1) })
      const setter = isArchive ? setOldDedicaces : setDedicaces
      setter(prev => prev.map(d => d.id === id ? { ...d, likes: (d.likes || 0) + 1 } : d))
    } catch (err) { console.error(err) }
  }

  const current = activeTab === 'recent' ? dedicaces : oldDedicaces

  const filtered = current.filter(d => {
    const s = searchTerm.toLowerCase()
    return !s ||
      d.from?.toLowerCase().includes(s) ||
      d.to?.toLowerCase().includes(s) ||
      d.message?.toLowerCase().includes(s) ||
      d.song?.toLowerCase().includes(s)
  })

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE)
  const paginated  = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)

  return (
    <div className="page active">
      <div style={{
        maxWidth: '100%', width: '100%',
        padding: 'clamp(12px,2vw,24px)',
        paddingBottom: 80, boxSizing: 'border-box'
      }}>

        {/* HEADER */}
        <div className="page-header" style={{ marginBottom: 'clamp(14px,2.5vh,22px)' }}>
          <div className="page-badge">💌 DÉDICACES</div>
          <h1 className="page-title">Dédicaces</h1>
          <p className="page-subtitle">Pour un ami, pour l'amour de Johnny</p>
        </div>

        {/* Bouton nouvelle dédicace */}
        {currentUser && (
          <div style={{ marginBottom: 'clamp(14px,2.5vh,20px)', textAlign: 'center' }}>
            <button
              onClick={() => setShowForm(v => !v)}
              className="btn btn-primary"
              style={{ padding: 'clamp(12px,2vh,16px) clamp(20px,3.5vw,30px)', fontSize: 'clamp(0.92rem,1.8vw,1.05rem)', display: 'inline-flex', alignItems: 'center', gap: 10 }}
            >
              <Send size={18} /> {showForm ? 'Annuler' : 'Envoyer une dédicace'}
            </button>
          </div>
        )}

        {/* Formulaire */}
        {showForm && currentUser && (
          <div className="glass-card" style={{ marginBottom: 'clamp(16px,2.8vh,24px)', display: 'flex', flexDirection: 'column', gap: 'clamp(14px,2.5vh,20px)' }}>
            <h2 style={{ fontSize: 'clamp(1.1rem,2.3vw,1.4rem)', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Heart size={22} color="#ff1493" /> Nouvelle Dédicace
            </h2>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px,2vh,16px)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(10px,1.8vw,14px)' }}>
                <Field label="De (votre prénom)" required>
                  <input
                    type="text" value={formData.from} required placeholder="David"
                    onChange={e => setFormData(p => ({ ...p, from: e.target.value }))}
                    style={inputStyle}
                    onFocus={e => e.target.style.borderColor = 'rgba(255,20,147,0.5)'}
                    onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
                  />
                </Field>
                <Field label="À (destinataire)" required>
                  <input
                    type="text" value={formData.to} required placeholder="À tous les fans"
                    onChange={e => setFormData(p => ({ ...p, to: e.target.value }))}
                    style={inputStyle}
                    onFocus={e => e.target.style.borderColor = 'rgba(0,217,255,0.5)'}
                    onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
                  />
                </Field>
              </div>

              <Field label={`Message (${formData.message.length}/200)`} required>
                <textarea
                  value={formData.message} required rows={4} maxLength={200}
                  placeholder="Partagez votre amour pour Johnny..."
                  onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
                  style={{ ...inputStyle, resize: 'vertical' }}
                  onFocus={e => e.target.style.borderColor = 'rgba(255,20,147,0.5)'}
                  onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
                />
                <div style={{ textAlign: 'right', fontSize: '0.78rem', color: formData.message.length >= 180 ? '#ff1493' : 'rgba(255,255,255,0.4)', fontWeight: formData.message.length >= 180 ? 700 : 400 }}>
                  {200 - formData.message.length} caractères restants
                </div>
              </Field>

              <Field label="Chanson (optionnel)">
                <input
                  type="text" value={formData.song} placeholder="Allumer le feu"
                  onChange={e => setFormData(p => ({ ...p, song: e.target.value }))}
                  style={inputStyle}
                  onFocus={e => e.target.style.borderColor = 'rgba(255,215,0,0.5)'}
                  onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.15)'}
                />
              </Field>

              <button
                type="submit" disabled={submitting}
                className="btn btn-primary"
                style={{ width: '100%', padding: 'clamp(12px,2vh,16px)', fontSize: 'clamp(0.92rem,1.8vw,1.05rem)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, opacity: submitting ? 0.6 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
              >
                <Send size={18} /> {submitting ? 'Envoi en cours...' : 'Envoyer ma dédicace'}
              </button>

              <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center', margin: 0 }}>
                ⓘ Votre dédicace sera visible après validation par notre équipe
              </p>
            </form>
          </div>
        )}

        {/* ONGLETS */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 'clamp(12px,2vh,18px)' }}>
          {[
            { id: 'recent',   label: `📨 Récentes`, count: dedicaces.length   },
            { id: 'archives', label: `📚 Archives`,  count: oldDedicaces.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setCurrentPage(1); setSearchTerm('') }}
              style={{
                padding: 'clamp(12px,2vh,16px)',
                background: activeTab === tab.id ? 'linear-gradient(135deg,#00d9ff,#ffd700)' : 'rgba(255,255,255,0.05)',
                border: activeTab === tab.id ? 'none' : '1px solid rgba(255,255,255,0.15)',
                borderRadius: 14,
                color: activeTab === tab.id ? '#000' : '#fff',
                fontWeight: 700, fontSize: 'clamp(0.88rem,1.8vw,1rem)',
                cursor: 'pointer', transition: 'all 0.25s'
              }}
            >
              {tab.label} <span style={{ opacity: 0.7 }}>({tab.count})</span>
            </button>
          ))}
        </div>

        {/* RECHERCHE */}
        <div className="glass-card" style={{ padding: 'clamp(10px,1.8vh,14px)', marginBottom: 'clamp(12px,2vh,18px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Search size={17} color="rgba(255,255,255,0.4)" />
            <input
              type="text" placeholder="Rechercher une dédicace..."
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1) }}
              style={{ flex: 1, background: 'transparent', border: 'none', color: '#fff', outline: 'none', fontSize: 'clamp(0.88rem,1.7vw,1rem)' }}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>
            )}
          </div>
        </div>

        {/* LISTE */}
        {loading ? (
          <div className="loading-spinner"><div className="spinner" /><p>Chargement...</p></div>
        ) : paginated.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">💌</div>
            <p>{searchTerm ? 'Aucune dédicace trouvée' : 'Aucune dédicace pour le moment'}</p>
          </div>
        ) : (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(280px, 40%, 480px), 1fr))',
              gap: 'clamp(10px, 1.8vw, 16px)',
              marginBottom: 'clamp(16px, 2.8vh, 24px)'
            }}>
              {paginated.map(dedicace => (
                <DedicaceCard
                  key={dedicace.id}
                  dedicace={dedicace}
                  isArchive={activeTab === 'archives'}
                  onLike={handleLike}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  style={{ opacity: currentPage === 1 ? 0.45 : 1 }}
                >
                  ← Précédent
                </button>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                  Page {currentPage} / {totalPages}
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  style={{ opacity: currentPage === totalPages ? 0.45 : 1 }}
                >
                  Suivant →
                </button>
              </div>
            )}

            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>
              {filtered.length} dédicace{filtered.length > 1 ? 's' : ''} trouvée{filtered.length > 1 ? 's' : ''}
            </div>
          </>
        )}

      </div>
    </div>
  )
}

export default DedicacesPage